/**
 * Endpoints de los catálogos de Configuración (`/api/bancos`,
 * `/api/cuentas_bancarias`, `/api/tarjetas` y `/api/monedas`).
 *
 * Los cuatro son del grupo `configuration`, igual que el resto de la pantalla.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  Banco,
  BancosRespuesta,
  CuentaPayload,
  CuentasRespuesta,
  MetodoDetraccion,
  MetodoPago,
  MetodoPagoPayload,
  MetodosDetraccionRespuesta,
  MetodosPagoRespuesta,
  Moneda,
  MonedaPayload,
  MonedasRespuesta,
  PagosCpeRespuesta,
  SerieDocumento,
  SerieDocumentoPayload,
  SeriesDocumentoRespuesta,
  TagsNfcRespuesta,
  Tarjeta,
  TarjetaPayload,
  TarjetasRespuesta,
  TipoDetraccion,
  TipoDocumento,
  TipoDocumentoPayload,
  TiposDetraccionRespuesta,
  TiposDocumentoRespuesta,
  Turno,
  TurnoPayload,
  TurnosRespuesta,
} from './catalogos.types';

export const catalogoKeys = {
  bancos: ['catalogos', 'bancos'] as const,
  cuentas: ['catalogos', 'cuentas'] as const,
  tarjetas: ['catalogos', 'tarjetas'] as const,
  monedas: ['catalogos', 'monedas'] as const,
  tiposDocumento: ['catalogos', 'tipos-documento'] as const,
  seriesDocumento: ['catalogos', 'series-documento'] as const,
  metodosPago: ['catalogos', 'metodos-pago'] as const,
  metodosDetraccion: ['catalogos', 'metodos-pago-detraccion'] as const,
  tiposDetraccion: ['catalogos', 'tipos-detraccion'] as const,
  turnos: ['catalogos', 'turnos'] as const,
  nfc: ['catalogos', 'nfc'] as const,
  pagosCpe: ['catalogos', 'pagos-cpe'] as const,
} as const;

export const catalogosApi = {
  // Bancos
  listarBancos: (signal?: AbortSignal): Promise<BancosRespuesta> =>
    apiClient.get<BancosRespuesta>('/api/bancos', { signal }),
  crearBanco: (descripcion: string): Promise<{ banco: Banco; mensaje: string }> =>
    apiClient.post<{ banco: Banco; mensaje: string }>('/api/bancos', { body: { descripcion } }),
  actualizarBanco: (
    id: number,
    payload: { descripcion?: string; activo?: boolean },
  ): Promise<{ banco: Banco; mensaje: string }> =>
    apiClient.put<{ banco: Banco; mensaje: string }>(`/api/bancos/${id}`, { body: payload }),
  eliminarBanco: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/bancos/${id}`),

  // Cuentas bancarias
  listarCuentas: (signal?: AbortSignal): Promise<CuentasRespuesta> =>
    apiClient.get<CuentasRespuesta>('/api/cuentas_bancarias', { signal }),
  crearCuenta: (payload: CuentaPayload): Promise<{ cuenta: CuentaPayload; mensaje: string }> =>
    apiClient.post<{ cuenta: CuentaPayload; mensaje: string }>('/api/cuentas_bancarias', {
      body: payload,
    }),
  actualizarCuenta: (
    id: number,
    payload: CuentaPayload,
  ): Promise<{ cuenta: CuentaPayload; mensaje: string }> =>
    apiClient.put<{ cuenta: CuentaPayload; mensaje: string }>(`/api/cuentas_bancarias/${id}`, {
      body: payload,
    }),
  eliminarCuenta: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/cuentas_bancarias/${id}`),

  // Tarjetas
  listarTarjetas: (signal?: AbortSignal): Promise<TarjetasRespuesta> =>
    apiClient.get<TarjetasRespuesta>('/api/tarjetas', { signal }),
  crearTarjeta: (payload: TarjetaPayload): Promise<{ tarjeta: Tarjeta; mensaje: string }> =>
    apiClient.post<{ tarjeta: Tarjeta; mensaje: string }>('/api/tarjetas', { body: payload }),
  actualizarTarjeta: (
    codigo: string,
    payload: { descripcion?: string; activo?: boolean },
  ): Promise<{ tarjeta: Tarjeta; mensaje: string }> =>
    apiClient.put<{ tarjeta: Tarjeta; mensaje: string }>(`/api/tarjetas/${codigo}`, {
      body: payload,
    }),
  eliminarTarjeta: (codigo: string): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/tarjetas/${codigo}`),

  // Monedas (sólo edición, como el panel anterior)
  listarMonedas: (signal?: AbortSignal): Promise<MonedasRespuesta> =>
    apiClient.get<MonedasRespuesta>('/api/monedas', { signal }),
  actualizarMoneda: (
    codigo: string,
    payload: MonedaPayload,
  ): Promise<{ moneda: Moneda; mensaje: string }> =>
    apiClient.put<{ moneda: Moneda; mensaje: string }>(`/api/monedas/${codigo}`, { body: payload }),

  // Tipos de documento
  listarTiposDocumento: (signal?: AbortSignal): Promise<TiposDocumentoRespuesta> =>
    apiClient.get<TiposDocumentoRespuesta>('/api/tipos_documento', { signal }),
  crearTipoDocumento: (
    payload: TipoDocumentoPayload,
  ): Promise<{ tipo: TipoDocumento; mensaje: string }> =>
    apiClient.post<{ tipo: TipoDocumento; mensaje: string }>('/api/tipos_documento', {
      body: payload,
    }),
  actualizarTipoDocumento: (
    id: number,
    payload: TipoDocumentoPayload,
  ): Promise<{ tipo: TipoDocumento; mensaje: string }> =>
    apiClient.put<{ tipo: TipoDocumento; mensaje: string }>(`/api/tipos_documento/${id}`, {
      body: payload,
    }),
  cambiarEstadoTipoDocumento: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/tipos_documento/${id}/estado`, {
      body: { activo },
    }),

  // Series de documento
  listarSeriesDocumento: (signal?: AbortSignal): Promise<SeriesDocumentoRespuesta> =>
    apiClient.get<SeriesDocumentoRespuesta>('/api/series_documento', { signal }),
  crearSerieDocumento: (
    payload: SerieDocumentoPayload,
  ): Promise<{ serie: SerieDocumento; mensaje: string }> =>
    apiClient.post<{ serie: SerieDocumento; mensaje: string }>('/api/series_documento', {
      body: payload,
    }),
  actualizarSerieDocumento: (
    id: number,
    payload: SerieDocumentoPayload,
  ): Promise<{ serie: SerieDocumento; mensaje: string }> =>
    apiClient.put<{ serie: SerieDocumento; mensaje: string }>(`/api/series_documento/${id}`, {
      body: payload,
    }),
  cambiarEstadoSerieDocumento: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/series_documento/${id}/estado`, {
      body: { activo },
    }),

  // Métodos de pago
  listarMetodosPago: (signal?: AbortSignal): Promise<MetodosPagoRespuesta> =>
    apiClient.get<MetodosPagoRespuesta>('/api/metodos_pago', { signal }),
  crearMetodoPago: (payload: MetodoPagoPayload): Promise<{ metodo: MetodoPago; mensaje: string }> =>
    apiClient.post<{ metodo: MetodoPago; mensaje: string }>('/api/metodos_pago', { body: payload }),
  actualizarMetodoPago: (
    id: number,
    payload: MetodoPagoPayload,
  ): Promise<{ metodo: MetodoPago; mensaje: string }> =>
    apiClient.put<{ metodo: MetodoPago; mensaje: string }>(`/api/metodos_pago/${id}`, {
      body: payload,
    }),
  cambiarEstadoMetodoPago: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/metodos_pago/${id}/estado`, { body: { activo } }),

  // Métodos de pago de detracción
  listarMetodosDetraccion: (signal?: AbortSignal): Promise<MetodosDetraccionRespuesta> =>
    apiClient.get<MetodosDetraccionRespuesta>('/api/metodos_pago_detraccion', { signal }),
  crearMetodoDetraccion: (payload: {
    codigo: string;
    descripcion: string;
  }): Promise<{ metodo: MetodoDetraccion; mensaje: string }> =>
    apiClient.post<{ metodo: MetodoDetraccion; mensaje: string }>('/api/metodos_pago_detraccion', {
      body: payload,
    }),
  actualizarMetodoDetraccion: (
    id: number,
    payload: { codigo: string; descripcion: string },
  ): Promise<{ metodo: MetodoDetraccion; mensaje: string }> =>
    apiClient.put<{ metodo: MetodoDetraccion; mensaje: string }>(
      `/api/metodos_pago_detraccion/${id}`,
      { body: payload },
    ),
  cambiarEstadoMetodoDetraccion: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/metodos_pago_detraccion/${id}/estado`, {
      body: { activo },
    }),

  // Tipos de detracción
  listarTiposDetraccion: (signal?: AbortSignal): Promise<TiposDetraccionRespuesta> =>
    apiClient.get<TiposDetraccionRespuesta>('/api/tipos_detraccion', { signal }),
  crearTipoDetraccion: (payload: {
    codigo: string;
    descripcion: string;
    porcentaje: number;
  }): Promise<{ tipo: TipoDetraccion; mensaje: string }> =>
    apiClient.post<{ tipo: TipoDetraccion; mensaje: string }>('/api/tipos_detraccion', {
      body: payload,
    }),
  actualizarTipoDetraccion: (
    id: number,
    payload: { codigo: string; descripcion: string; porcentaje: number },
  ): Promise<{ tipo: TipoDetraccion; mensaje: string }> =>
    apiClient.put<{ tipo: TipoDetraccion; mensaje: string }>(`/api/tipos_detraccion/${id}`, {
      body: payload,
    }),
  cambiarEstadoTipoDetraccion: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/tipos_detraccion/${id}/estado`, { body: { activo } }),

  // Turnos (único catálogo con borrado)
  listarTurnos: (signal?: AbortSignal): Promise<TurnosRespuesta> =>
    apiClient.get<TurnosRespuesta>('/api/turnos_catalogo', { signal }),
  crearTurno: (payload: TurnoPayload): Promise<{ turno: Turno; mensaje: string }> =>
    apiClient.post<{ turno: Turno; mensaje: string }>('/api/turnos_catalogo', { body: payload }),
  actualizarTurno: (
    id: number,
    payload: TurnoPayload,
  ): Promise<{ turno: Turno; mensaje: string }> =>
    apiClient.put<{ turno: Turno; mensaje: string }>(`/api/turnos_catalogo/${id}`, { body: payload }),
  eliminarTurno: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/turnos_catalogo/${id}`),

  // Dispositivos NFC (sólo listado y estado)
  listarTagsNfc: (signal?: AbortSignal): Promise<TagsNfcRespuesta> =>
    apiClient.get<TagsNfcRespuesta>('/api/nfc_tags', { signal }),
  cambiarEstadoTagNfc: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/nfc_tags/${id}/estado`, { body: { activo } }),

  // Pagos CPE
  listarPagosCpe: (signal?: AbortSignal): Promise<PagosCpeRespuesta> =>
    apiClient.get<PagosCpeRespuesta>('/api/pagos_cpe', { signal }),
  guardarPagosCpe: (
    metodos: { id: number; tipo: number | null; destino: number | null }[],
  ): Promise<PagosCpeRespuesta & { mensaje: string }> =>
    apiClient.put<PagosCpeRespuesta & { mensaje: string }>('/api/pagos_cpe', { body: { metodos } }),
} as const;

export function useBancos(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.bancos,
    queryFn: ({ signal }) => catalogosApi.listarBancos(signal),
    enabled,
    staleTime: 30_000,
  });
}

export function useCuentasBancarias(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.cuentas,
    queryFn: ({ signal }) => catalogosApi.listarCuentas(signal),
    enabled,
    staleTime: 30_000,
  });
}

export function useTarjetas(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.tarjetas,
    queryFn: ({ signal }) => catalogosApi.listarTarjetas(signal),
    enabled,
    staleTime: 30_000,
  });
}

export function useMonedas(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.monedas,
    queryFn: ({ signal }) => catalogosApi.listarMonedas(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Tipos de documento (también los usa el formulario de series). */
export function useTiposDocumento(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.tiposDocumento,
    queryFn: ({ signal }) => catalogosApi.listarTiposDocumento(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Series de documento. */
export function useSeriesDocumento(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.seriesDocumento,
    queryFn: ({ signal }) => catalogosApi.listarSeriesDocumento(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Métodos de pago. */
export function useMetodosPago(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.metodosPago,
    queryFn: ({ signal }) => catalogosApi.listarMetodosPago(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Métodos de pago para detracción. */
export function useMetodosDetraccion(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.metodosDetraccion,
    queryFn: ({ signal }) => catalogosApi.listarMetodosDetraccion(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Tipos de detracción. */
export function useTiposDetraccion(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.tiposDetraccion,
    queryFn: ({ signal }) => catalogosApi.listarTiposDetraccion(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Turnos (catálogo). */
export function useTurnosCatalogo(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.turnos,
    queryFn: ({ signal }) => catalogosApi.listarTurnos(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Dispositivos NFC. */
export function useTagsNfc(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.nfc,
    queryFn: ({ signal }) => catalogosApi.listarTagsNfc(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Métodos de pago del CPE (sólo administradores). */
export function usePagosCpe(enabled = true) {
  return useQuery({
    queryKey: catalogoKeys.pagosCpe,
    queryFn: ({ signal }) => catalogosApi.listarPagosCpe(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Mutaciones de bancos. */
export function useMutarBancos() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.bancos });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearBanco, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, descripcion }: { id: number; descripcion: string }) =>
        catalogosApi.actualizarBanco(id, { descripcion }),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({ mutationFn: catalogosApi.eliminarBanco, onSuccess: refrescar }),
  };
}

/** Mutaciones de cuentas bancarias. */
export function useMutarCuentas() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.cuentas });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearCuenta, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: CuentaPayload }) =>
        catalogosApi.actualizarCuenta(id, payload),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({ mutationFn: catalogosApi.eliminarCuenta, onSuccess: refrescar }),
  };
}

/** Mutaciones de tarjetas. */
export function useMutarTarjetas() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.tarjetas });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearTarjeta, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ codigo, descripcion }: { codigo: string; descripcion: string }) =>
        catalogosApi.actualizarTarjeta(codigo, { descripcion }),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({ mutationFn: catalogosApi.eliminarTarjeta, onSuccess: refrescar }),
  };
}

/** Mutación de monedas (sólo edición). */
export function useMutarMonedas() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.monedas });

  return {
    actualizar: useMutation({
      mutationFn: ({ codigo, payload }: { codigo: string; payload: MonedaPayload }) =>
        catalogosApi.actualizarMoneda(codigo, payload),
      onSuccess: refrescar,
    }),
  };
}

/** Mutaciones de tipos de documento (no hay borrado: activar/desactivar). */
export function useMutarTiposDocumento() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.tiposDocumento });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearTipoDocumento, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: TipoDocumentoPayload }) =>
        catalogosApi.actualizarTipoDocumento(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        catalogosApi.cambiarEstadoTipoDocumento(id, activo),
      onSuccess: refrescar,
    }),
  };
}

/** Mutaciones de series de documento (no hay borrado: activar/desactivar). */
export function useMutarSeriesDocumento() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.seriesDocumento });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearSerieDocumento, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: SerieDocumentoPayload }) =>
        catalogosApi.actualizarSerieDocumento(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        catalogosApi.cambiarEstadoSerieDocumento(id, activo),
      onSuccess: refrescar,
    }),
  };
}

/** Mutaciones de métodos de pago (no hay borrado: activar/desactivar). */
export function useMutarMetodosPago() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.metodosPago });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearMetodoPago, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: MetodoPagoPayload }) =>
        catalogosApi.actualizarMetodoPago(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        catalogosApi.cambiarEstadoMetodoPago(id, activo),
      onSuccess: refrescar,
    }),
  };
}

/** Mutaciones de métodos de pago de detracción. */
export function useMutarMetodosDetraccion() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.metodosDetraccion });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearMetodoDetraccion, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: { codigo: string; descripcion: string } }) =>
        catalogosApi.actualizarMetodoDetraccion(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        catalogosApi.cambiarEstadoMetodoDetraccion(id, activo),
      onSuccess: refrescar,
    }),
  };
}

/** Mutaciones de tipos de detracción. */
export function useMutarTiposDetraccion() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.tiposDetraccion });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearTipoDetraccion, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({
        id,
        payload,
      }: {
        id: number;
        payload: { codigo: string; descripcion: string; porcentaje: number };
      }) => catalogosApi.actualizarTipoDetraccion(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        catalogosApi.cambiarEstadoTipoDetraccion(id, activo),
      onSuccess: refrescar,
    }),
  };
}

/** Mutaciones de turnos (el único catálogo con borrado). */
export function useMutarTurnos() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.turnos });

  return {
    crear: useMutation({ mutationFn: catalogosApi.crearTurno, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: TurnoPayload }) =>
        catalogosApi.actualizarTurno(id, payload),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({ mutationFn: catalogosApi.eliminarTurno, onSuccess: refrescar }),
  };
}

/** Mutaciones de dispositivos NFC (sólo el estado). */
export function useMutarTagsNfc() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: catalogoKeys.nfc });

  return {
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        catalogosApi.cambiarEstadoTagNfc(id, activo),
      onSuccess: refrescar,
    }),
  };
}

/** Mutación de Pagos CPE (se guardan todos los métodos de una vez). */
export function useMutarPagosCpe() {
  const cliente = useQueryClient();

  return {
    guardar: useMutation({
      mutationFn: (metodos: { id: number; tipo: number | null; destino: number | null }[]) =>
        catalogosApi.guardarPagosCpe(metodos),
      onSuccess: (datos) => cliente.setQueryData(catalogoKeys.pagosCpe, datos),
    }),
  };
}
